import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { AuthContext } from '@/contexts/AuthContext';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styles from './Catalog.module.css';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi } from '@/hooks/usePaginatedApi';
import { CarDTO, CustomCarResourceService } from '@/openapi';
import Loader from '@/components/UI/Loader';
import CarCard from '@/components/CarCard';
import Pagination from '@/components/UI/Paging';
import { useRemoteGet } from '@/hooks/useRemoteGet';

const Catalog = () => {
	const CATALOG_CARS_CONTEXT_ID = 'catalog-cars';
	const { t } = useTranslation();

	const authCtx = useContext(AuthContext);

	// Stati per i filtri
	const [filters, setFilters] = useState({
		category: 'RENTAL' as const,
		maxPrice: null as number | null,
		brand: '',
		doors: null as number | null,
		transmission: '',
		fuelType: ''
	});

	const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [debouncedFilters, setDebouncedFilters] = useState(filters);

	const getMaxMonthlyPrice = useRemoteGet<number>({
		remoteMethod: async () => {
			return CustomCarResourceService.getMaxPrice({
				category: filters.category
			});
		},
		lazy: true
	});

	const cars = usePaginatedApi<CarDTO>(
		async (pagingOptions) => {
			return CustomCarResourceService.getAdvancedFilteredOffers({
				category: debouncedFilters.category,
				maxPrice: debouncedFilters.maxPrice || undefined,
				brand: debouncedFilters.brand || undefined,
				doors: debouncedFilters.doors || undefined,
				transmission: debouncedFilters.transmission || undefined,
				fuelType: debouncedFilters.fuelType || undefined,
				page: pagingOptions.page,
				size: pagingOptions.size,
				sortBy: pagingOptions.sortBy,
				sortDir: pagingOptions.sortDir
			});
		},
		{
			page: 0,
			size: 9,
			sortBy: 'brand',
			sortDir: 'asc'
		},
		CATALOG_CARS_CONTEXT_ID
	);

	const maxPriceLimit = getMaxMonthlyPrice.data ?? 150;

	useEffect(() => {
		getMaxMonthlyPrice.fetch();
	}, [authCtx.userId]);

	// Ad ogni aggiornamento dei filtri parte la fetch
	useEffect(() => {
		if (debouncedFilters.maxPrice !== null) {
			cars.fetch();
		}
	}, [debouncedFilters]);

	useEffect(() => {
		getMaxMonthlyPrice.fetch();
	}, [filters.category]);

	// Quando arriva il maxPrice, aggiorna il filtro E poi fai la fetch
	useEffect(() => {
		if (getMaxMonthlyPrice.data && typeof getMaxMonthlyPrice.data === 'number') {
			setFilters((prev) => {
				const newFilters = {
					...prev,
					maxPrice: getMaxMonthlyPrice.data!
				};
				// Aggiorna anche i filtri debouncati immediatamente per questa modifica
				setDebouncedFilters(newFilters);
				return newFilters;
			});
		}
	}, [getMaxMonthlyPrice.data]);

	useEffect(() => {
		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, []);

	const debouncedSetFilters = useCallback((newFilters: typeof filters) => {
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(() => {
			setDebouncedFilters(newFilters);
		}, 1500);
	}, []);

	const handleFilterChange = (key: string, value: any) => {
		const newFilters = {
			...filters,
			[key]: value
		};

		setFilters(newFilters);

		// Per il cambio di categoria, resetta maxPrice e non aggiornare debouncedFilters
		if (key === 'category') {
			const filtersWithNullPrice = {
				...newFilters,
				maxPrice: null
			};
			setFilters(filtersWithNullPrice);
			setDebouncedFilters(filtersWithNullPrice);
			return; // Esci qui, il nuovo maxPrice arriverà dall'useEffect
		}

		// Applica debounce solo per brand e maxPrice
		if (key === 'brand' || key === 'maxPrice') {
			debouncedSetFilters(newFilters);
		} else {
			// Per altri filtri, aggiorna immediatamente
			setDebouncedFilters(newFilters);
		}
	};

	const resetFilters = () => {
		// Cancella il timeout del debounce se attivo
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		const newFilters = {
			category: 'RENTAL' as const,
			maxPrice: null as number | null,
			brand: '',
			doors: null,
			transmission: '',
			fuelType: ''
		};

		setFilters(newFilters);
		setDebouncedFilters(newFilters);

		// Dopo il reset, ricarica il prezzo massimo
		getMaxMonthlyPrice.fetch();
	};

	return (
		<div className={styles.main_home_container}>
			<Navbar className={styles.navDivider} />

			<main className={styles.mainContent}>
				<Loader visible={cars.loading} />

				<section className={styles.heroSection}>
					<div className={styles.heroContainer}>
						<div className={styles.heroContent}>
							<h1 className={styles.catalogTitle}>{t('catalog.title')}</h1>
							<div className={styles.resultsCount}>
								<div className={styles.nrResultBadge}>
									{cars.totalElements || 0} {t('catalog.gridCatalog.results')}
								</div>
							</div>
						</div>
					</div>
				</section>

				<div className={styles.mainCatalogContainer}>
					{/* Filtri principali */}
					<div className={styles.filtersHeader}>
						<div className={styles.mainFilters}>
							<div className={styles.filterGroup}>
								<label className={styles.filterLabel}>{t('catalog.filters.category')}</label>
								<div className={styles.filterContainer}>
									<select
										className={styles.filterSelect}
										value={filters.category}
										onChange={(e) => handleFilterChange('category', e.target.value)}
									>
										<option value="RENTAL">{t('catalog.filters.rental').toUpperCase()}</option>
										<option value="USED">{t('catalog.filters.used').toUpperCase()}</option>
										{/*
										<option value="VARIOUS">{t('catalog.filters.various').toUpperCase()}</option>
										*/}
									</select>
								</div>
							</div>

							<div className={styles.filterGroup}>
								<label className={styles.filterLabel}>{t('catalog.filters.brand')}</label>
								<div className={styles.filterContainer}>
									<input
										type="text"
										className={styles.filterInput}
										placeholder={t('catalog.filters.brandPlaceholder')}
										value={filters.brand}
										onChange={(e) => handleFilterChange('brand', e.target.value)}
									/>
								</div>
							</div>

							<div className={styles.filterGroup}>
								{t('catalog.filters.maxPrice', { price: filters.maxPrice })}
								<div className={styles.filterContainer}>
									<input
										type="range"
										className={styles.priceSlider}
										min="100"
										max={maxPriceLimit}
										step="1"
										value={filters.maxPrice || 100}
										onChange={(e) => handleFilterChange('maxPrice', parseInt(e.target.value))}
										style={{
											background: `linear-gradient(to right, #ea5b0c 0%, #ea5b0c ${
												(((filters.maxPrice || 100) - 100) / (maxPriceLimit - 100)) * 100
											}%, #e5e5e5 ${(((filters.maxPrice || 100) - 100) / (maxPriceLimit - 100)) * 100}%, #e5e5e5 100%)`
										}}
									/>
								</div>
							</div>

							<div className={styles.filterGroup}>
								<label className={styles.filterLabel}>{t('catalog.filters.advancedSearch')}</label>
								<div className={styles.filterContainer}>
									<label className={styles.switch}>
										<input
											type="checkbox"
											checked={showAdvancedFilters}
											onChange={(e) => setShowAdvancedFilters(e.target.checked)}
										/>
										<span className={styles.slider}></span>
									</label>
								</div>
							</div>
						</div>
					</div>

					<div className={styles.catalogLayout}>
						{/* Sidebar filtri avanzati */}
						{showAdvancedFilters && (
							<div className={styles.advancedFiltersSidebar}>
								<div className={styles.sidebarHeader}>
									<h3>{t('catalog.advanced.title')}</h3>
									<button className={styles.resetButton} onClick={resetFilters}>
										{t('catalog.advanced.reset')}
									</button>
								</div>

								<div className={styles.filterGroupAdvanced}>
									<label className={styles.filterLabel}>{t('catalog.advanced.sortPrice')}</label>
									<div className={styles.filterContainer}>
										<select
											className={styles.filterSelect}
											onChange={(e) => {
												const [sortBy, sortDir] = e.target.value.split('-');
												cars.setPagingOptions({
													sortBy,
													sortDir: sortDir as 'asc' | 'desc'
												});
											}}
										>
											<option value="brand-asc">{t('catalog.advanced.sortDefault')}</option>
											<option value="pricePerMonth-asc">{t('catalog.advanced.sortPriceAsc')}</option>
											<option value="pricePerMonth-desc">{t('catalog.advanced.sortPriceDesc')}</option>
										</select>
									</div>
								</div>

								<div className={styles.filterGroupAdvanced}>
									<label className={styles.filterLabel}>{t('catalog.advanced.doors')}</label>
									<div className={styles.checkboxGroup}>
										{[3, 5].map((door) => (
											<label key={door} className={styles.checkboxLabel}>
												<input
													type="radio"
													name="doors"
													checked={filters.doors === door}
													onChange={() => handleFilterChange('doors', door)}
												/>
												{t('catalog.advanced.doorsCount', { count: door })}
											</label>
										))}
										<label className={styles.checkboxLabel}>
											<input
												type="radio"
												name="doors"
												checked={filters.doors === null}
												onChange={() => handleFilterChange('doors', null)}
											/>
											{t('catalog.advanced.all')}
										</label>
									</div>
								</div>

								<div className={styles.filterGroupAdvanced}>
									<label className={styles.filterLabel}>{t('catalog.advanced.transmission')}</label>
									<div className={styles.checkboxGroup}>
										{['Manuale', 'Automatico'].map((trans) => (
											<label key={trans} className={styles.checkboxLabel}>
												<input
													type="radio"
													name="transmission"
													checked={filters.transmission === trans}
													onChange={() => handleFilterChange('transmission', trans)}
												/>
												{trans}
											</label>
										))}
										<label className={styles.checkboxLabel}>
											<input
												type="radio"
												name="transmission"
												checked={filters.transmission === ''}
												onChange={() => handleFilterChange('transmission', '')}
											/>
											{t('catalog.advanced.all')}
										</label>
									</div>
								</div>

								<div className={styles.filterGroupAdvanced}>
									<label className={styles.filterLabel}>{t('catalog.advanced.fuel')}</label>
									<div className={styles.checkboxGroup}>
										{['Benzina', 'Diesel', 'Mild Hybrid'].map((fuel) => (
											<label key={fuel} className={styles.checkboxLabel}>
												<input
													type="radio"
													name="fuelType"
													checked={filters.fuelType === fuel}
													onChange={() => handleFilterChange('fuelType', fuel)}
												/>
												{fuel}
											</label>
										))}
										<label className={styles.checkboxLabel}>
											<input
												type="radio"
												name="fuelType"
												checked={filters.fuelType === ''}
												onChange={() => handleFilterChange('fuelType', '')}
											/>
											{t('catalog.advanced.all')}
										</label>
									</div>
								</div>
							</div>
						)}

						{/* Contenuto principale */}
						<div className={styles.catalogContent}>
							<div className={styles.paginatedGrid}>
								<div className={styles.gridContainer}>
									{cars.data.map((car) => (
										<CarCard
											key={car.id}
											carData={car}
											firstImageId={car.imageIds && car.imageIds.length > 0 ? car.imageIds[0] : null}
										/>
									))}
									{cars.data.length === 0 && <div className={styles.noResults}>{t('catalog.gridCatalog.noCarsAvailable')}</div>}
								</div>
								<Pagination contextId={CATALOG_CARS_CONTEXT_ID} />
							</div>
						</div>
					</div>
				</div>
			</main>

			<Footer />
		</div>
	);
};

export default Catalog;
